// Anonymous client-error reporting through the analytics pipeline.
//
// Crashes in players' browsers used to reach only their own dev-tools
// console. `reportError()` sends one `client_error` event per distinct
// error, through `track()`, so it inherits every analytics guarantee:
// no-op when analytics is disabled or the player opted out, and the
// property allow-list (error_kind / error_message / error_boundary / path).
//
// Privacy: the message is scrubbed of anything that could identify a
// player (emails, URL query strings and fragments, long digit runs, token-
// like strings) and capped; no stack traces, no user agent, no save data.
// A per-load cap stops a render loop from flooding the pipeline.

import { track } from "./analytics";

export type ErrorKind = "boundary" | "window_error" | "unhandled_rejection";

const MAX_REPORTS_PER_LOAD = 5;
const MAX_MESSAGE_LENGTH = 200;

let sentThisLoad = 0;
const seen = new Set<string>();

/** Strip anything that could identify a player from an error message. */
export function scrubErrorMessage(raw: string): string {
  return raw
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
    // Keep a URL's origin + path (useful: which chunk failed), drop ?query and #hash.
    .replace(/(https?:\/\/[^\s?#"']+)[?#][^\s"']*/gi, "$1")
    .replace(/\b[A-Za-z0-9_-]{32,}\b/g, "[token]")
    .replace(/\d{6,}/g, "[n]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_MESSAGE_LENGTH);
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

/**
 * Report an error once per distinct (kind, message) per page load, up to
 * MAX_REPORTS_PER_LOAD. Never throws: error reporting must not become a
 * second error.
 */
export function reportError(kind: ErrorKind, error: unknown, boundary?: string): void {
  try {
    if (sentThisLoad >= MAX_REPORTS_PER_LOAD) return;
    const message = scrubErrorMessage(messageOf(error));
    const key = `${kind}|${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    sentThisLoad++;
    track("client_error", {
      error_kind: kind,
      error_message: message,
      error_boundary: boundary ?? null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
    });
  } catch {
    // swallow: reporting is best-effort
  }
}

/** Test-only: reset the per-load cap and de-duplication set. */
export function _resetErrorReportingForTests() {
  sentThisLoad = 0;
  seen.clear();
}
